import axios from "axios";
import store from "@/store";
import { localStorageName } from '@/config/utils';

export const checkUrlExpiry = async (to, from, next) => {
  const { meta, params, name, query } = to;

  // Bug fix: on SPA navigation App.vue does not re-run, so the store's merchant/branch
  // codes can be empty — which makes localStorageName() resolve the wrong keys and leaves
  // views like Order with a null branch (stuck spinner). Backfill from the route params
  // when missing, without overriding values App.vue already set.
  if (store.state.merchantCode !== params.merchant_code && params.merchant_code) {
    store.dispatch("setMerchantCode", params.merchant_code);
  }
  if (store.state.branchCode !== params.branch_code && params.branch_code) {
    store.dispatch("setBranchCode", params.branch_code);
  }

  const isActive = meta && meta.active !== undefined ? meta.active : true;

  if (isActive) {
    try {
      if (query && query.queue) {
        if (!['Cover', 'Queue', 'Profile', 'MyCart', 'TopUp', 'MyTransactions', 'MyVouchers', 'LoyaltyTierBenefit', 'GetMemberShip', 'FindStores'].includes(name)) {
          next(false);
          return;
        }
      }

      if (params.type) {
        if (params.type.toString().toUpperCase() == 'QR') {
          let canNext = false;
          // Set when the 419 branch below already calls next() itself — the
          // fallback next() after the .then() must not also fire for it,
          // or Vue Router sees next() called twice for the same navigation.
          let handled = false;

          await axios.post(process.env.VUE_APP_BASE_URL + '/check-expiry-url', params,
            {
              headers: {
                "MerchantCode": (params.merchant_code || '').toLowerCase()
              }
            }
          )
          .then(
            (response) => {
              if (response.data.result.isSuccess) {
                let data = response.data.data;

                if (!data.accept_payment_on_moo) {
                  localStorage.removeItem('SELECTED_REWARD_' + params.merchant_code + '_' + params.branch_code);
                }

                const tableCode = params.table_code || query.table_code || '';
                if (tableCode) {
                  localStorage.removeItem('table_' + params.merchant_code.toLowerCase() + '_' + params.branch_code.toLowerCase() + '_' + tableCode.toLowerCase());
                  localStorage.setItem('table_' + params.merchant_code.toLowerCase() + '_' + params.branch_code.toLowerCase() + '_' + tableCode.toLowerCase(), JSON.stringify(data));
                }
                canNext = true;
              } else {
                if(name && name == "PaymentSuccess") {
                  canNext = true;
                } else {
                  // A clean 200 response saying "not valid" (e.g. QR not
                  // usable for online ordering) — show the real reason
                  // instead of whatever setError last held (or nothing).
                  store.dispatch("setError", {
                    statusCode: response.data.result.statusCode,
                    message: response.data.result.message || 'Page not found',
                    returnPath: to.fullPath,
                  });
                }
              }
            },
            (error) => {
              console.log("error", error);

              if (error.response?.status == 419) {
                handled = true;
                store.dispatch("setError", { ...error.response?.data?.result, returnPath: to.fullPath })

                if(name && name == "PaymentSuccess") {
                  next(true);
                } else {
                  next({ name: 'PageExpired' });
                }
              } else if (error.response) {
                // Server responded with a non-2xx status but a real body —
                // a genuine business/not-found error, not a connectivity
                // problem, so show its real message and don't offer a
                // pointless retry.
                const result = error.response.data?.result;
                store.dispatch("setError", {
                  statusCode: result?.statusCode ?? error.response.status,
                  message: result?.message || 'Page not found',
                  returnPath: to.fullPath,
                });
              } else {
                // No response at all — network drop, timeout, CORS, DNS failure.
                store.dispatch("setError", {
                  message: 'Unable to connect. Please check your connection and try again.',
                  retryable: true,
                  returnPath: to.fullPath,
                });
              }
            }
          )

          if (handled) {
            // Already routed above (PageExpired / PaymentSuccess) — calling
            // next() again here would be a second call for this navigation.
          } else if (canNext) {
            next(canNext);
          } else {
            next({ name: 'Error404' });
          }
        } else {
          next(false);
        }
      } else {
        if(name && name == "Cover") {
          var branch = JSON.parse(localStorage.getItem(localStorageName('BRANCH_INFO')) || 'null')

          // SH001: a salon branch always opens its own landing (the location picker), so
          // "Show Landing Page on Ordering Online = No" must not divert it.
          //
          // This guard decides off the *cached* branch_info, before App.vue's
          // /check-merchant-and-branch answers, so it can only redirect when the cache
          // positively says the branch is not a salon. A copy written before SH001 has no
          // salon_theme key at all — every shopper who had opened this branch before today
          // has one — and guessing "not salon" there bounced them to /order. When the flag
          // is unknown, leave the decision to Cover.vue, which reads branch_info again once
          // the API has answered and runs the same redirect for a non-salon branch.
          const salonKnown = branch && Object.prototype.hasOwnProperty.call(branch, 'salon_theme');
          const isNotSalonBranch = salonKnown && Number(branch.salon_theme) !== 1;

          if (branch && isNotSalonBranch && !branch.show_landing_page_on_ordering_online) {
            // Bug fix: next({ name }) alone doesn't inherit the current route's
            // params in Vue Router 4 — Order's required :merchant_code was
            // missing, throwing "Missing required param" and leaving a blank
            // page (the redirect silently failed). Pass params explicitly.
            next({ name: 'Order', params });
          } else {
            next(true);
          }
        } else {
          next(true);
        }
      }
    } catch (error) {
      console.log(error);
      next(false);
    }
  }
};

export const checkKioskMode = async (to, from, next) => {
  const { meta, params, name } = to;
  const isActive = meta?.active ?? true;

  if (!isActive) {
    return;
  }

  let response;
  let connectionError = null;

  try {
    response = await axios.post(
      `${process.env.VUE_APP_BASE_URL}/check-login-required`,
      params,
      {
        headers: {
          "MerchantCode": params.merchant_code.toLowerCase()
        }
      }
    );
  } catch (error) {
    connectionError = error;
  }

  // The redirect to /404 below drops merchant_code/branch_code from both the
  // URL and (via router's global beforeEach) localStorage, and App.vue skips
  // its merchant/branch check entirely while on /404 — so a plain reload from
  // the error page can't retry anything. Carry the original destination along
  // so Error404 can navigate straight back to it instead of reloading /404.
  const returnPath = to.fullPath;

  if (connectionError) {
    console.error('Error checking kiosk mode:', connectionError);

    if (connectionError.response) {
      // The server responded (with a non-2xx status, which axios treats as
      // a rejection) — this is a genuine business error like "Branch not
      // found", not a connectivity problem, so show its real message and
      // don't offer a pointless retry.
      const result = connectionError.response.data?.result;
      store.dispatch('setError', {
        statusCode: result?.statusCode ?? connectionError.response.status,
        message: result?.message || 'Page not found',
        returnPath,
      });
    } else {
      // No response at all — network drop, timeout, CORS, DNS failure.
      store.dispatch('setError', {
        message: 'Unable to connect. Please check your connection and try again.',
        retryable: true,
        returnPath,
      });
    }

    return next({ name: 'Error404' });
  }

  if (!response.data.result.isSuccess) {
    store.dispatch('setError', {
      statusCode: response.data.result.statusCode,
      message: response.data.result.message || 'Page not found',
      returnPath,
    });
    return next({ name: 'Error404' });
  }

  const { enable_retail_mode } = response.data.data;
  const kioskModeKey = `KIOSK_MODE_${params.merchant_code}_${params.branch_code}`;

  localStorage.setItem(kioskModeKey, enable_retail_mode);

  // Landing/Header read retail mode from the cached BRANCH_INFO, which goes
  // stale when the merchant switches the branch to/from retail while the kiosk
  // is open — keep it in sync with the fresh flag.
  const branchInfoKey = `BRANCH_INFO_${params.merchant_code}_${params.branch_code}`;
  var branch = JSON.parse(localStorage.getItem(branchInfoKey) || 'null');

  if (branch && branch.enable_retail_mode != enable_retail_mode) {
    branch.enable_retail_mode = enable_retail_mode;
    localStorage.setItem(branchInfoKey, JSON.stringify(branch));
  }

  // Retail mode has no menu page — send the guest to the retail checkout
  // instead of a 404 (KSK012/KSK019).
  if (enable_retail_mode && name === 'OrderPageKiosk') {
    return next({ name: 'CheckoutPageKiosk', params });
  }

  if (branch && branch.require_passcode_entry_to_redeem_vouchers) {
    const passcodeConfirmed = localStorage.getItem(`PASSCODE_CONFIRMED_${params.merchant_code}_${params.branch_code}`) ?? 0;
    if (passcodeConfirmed == 0 && name === 'MyRewardPageKiosk') {
      store.dispatch('setError', { returnPath });
      return next({ name: 'Error404' });
    }
  }

  return next();
};
